import "@wa/tokens/tokens.css";
import "@wa/ui/styles.css";
import "./index.css";

import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Loader, bindScrollbarKind } from "@wa/ui";
import { I18nProvider } from "./i18n/I18nProvider";
import { getMe, ADMIN_URL, withLang, type AccountUser } from "./lib/auth";
import { readFreshStash, clearStash } from "./lib/onboardingStash";
import { isPreview } from "./lib/previewMode";
import { api } from "./lib/api";
import { applyThemeColorVars, type SiteTheme } from "@wa/core/theme";
import { bindThemeModeEffects } from "./lib/themeMode";
import { applyBrandFavicon, brandTheme } from "./lib/brand";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ImportProgressPage } from "./pages/ImportProgressPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ConfirmDeletePage } from "./pages/ConfirmDeletePage";
import { PublicDocPage } from "./pages/publicDoc";
import { ConnectPage } from "./pages/connect";
import { saveConnectReturn, takeConnectReturn } from "./lib/connectReturn";
import { AccountLayout } from "./account/AccountLayout";
import { useLocation, useNavigate } from "react-router-dom";

function Splash() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background">
      <Loader size="lg" />
    </div>
  );
}

function App() {
  const [user, setUser] = useState<AccountUser | null | "loading">("loading");
  const [hostTheme, setHostTheme] = useState<SiteTheme | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Send an already-signed-in visitor to the admin centre's "New site" view with
  // the funnel's choices in hand (SSO on the same cookie): template/subdomain/name
  // PLUS the onboarding fields (onboard/caps/brief), so the Google and
  // already-logged-in paths do not lose the capabilities scaffold or the business
  // brief (`POST /admin/sites` applies them with the same logic the register uses).
  // `plan`/`cycle` travel ONLY on the Google-signup stash path - a paid plan chosen
  // in the funnel sends NewSitePage to Stripe checkout after creation, the same as
  // the e-mail register path - while the already-signed-in path carries no plan,
  // because that person bills through the account hub. `brief` can be personal data,
  // so NewSitePage strips it from the URL (§4).
  function forwardToNewSite(ctx: {
    template?: string;
    subdomain?: string;
    siteName?: string;
    onboard?: boolean;
    caps?: string;
    brief?: string;
    plan?: string;
    cycle?: string;
  }) {
    const q = new URLSearchParams();
    const template = (ctx.template ?? "").trim().slice(0, 60);
    const subdomain = (ctx.subdomain ?? "").toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40);
    const siteName = (ctx.siteName ?? "").trim().slice(0, 60);
    const plan = (ctx.plan ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40);
    if (template) q.set("template", template);
    if (subdomain) q.set("subdomain", subdomain);
    if (siteName) q.set("name", siteName);
    if (ctx.onboard) q.set("onboard", "1");
    if (ctx.caps) q.set("caps", ctx.caps.slice(0, 400));
    if (ctx.brief) q.set("brief", ctx.brief.slice(0, 400));
    if (plan) {
      q.set("plan", plan);
      if (ctx.cycle === "monthly" || ctx.cycle === "yearly") q.set("cycle", ctx.cycle);
    }
    window.location.href = withLang(`${ADMIN_URL}/sites/new?${q.toString()}`);
  }

  function redirectFromSignupFunnel() {
    const sp = new URLSearchParams(window.location.search);
    const plan = (sp.get("plan") ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40);
    const onSignup = window.location.pathname === "/signup";

    // Google signup is account-first (an account with no site). If the visitor came
    // from the public funnel, their choice is in the OAuth stash (sessionStorage, same
    // origin - the funnel's query string was lost on the Google redirect). The landing
    // is `?authOk=google&welcome=1`, where `welcome=1` means a NEW account and
    // therefore certainly no site. Consume the stash and create the first site with
    // the choice in hand. If the stash is missing or stale (the Google button pressed
    // from the login page, or another tab), STILL go to the "New site" view with an
    // empty form: a fresh account has no site, and an empty account hub would be a
    // dead end - the e-mail path forces the same step during signup.
    if (sp.get("authOk") === "google" && sp.get("welcome") === "1") {
      const stash = readFreshStash();
      clearStash();
      forwardToNewSite(stash ?? {});
      return;
    }

    // An intent to create a site, carried from the public funnel (path 1 "from
    // scratch" gives subdomain + siteName, path 2 "ready-made design" gives a
    // template). An already-signed-in visitor has an account, so send them to the
    // "New site" view with the choice in hand - otherwise the context is lost in the
    // overview.
    const template = (sp.get("template") ?? "").trim();
    const subdomain = (sp.get("subdomain") ?? "").trim();
    if (onSignup && (template || subdomain)) {
      forwardToNewSite({
        template,
        subdomain,
        siteName: sp.get("siteName") ?? undefined,
        onboard: sp.get("onboard") === "1",
        caps: sp.get("caps") ?? undefined,
        brief: sp.get("brief") ?? undefined,
      });
      return;
    }

    // Otherwise: an already-authenticated visitor arriving from a funnel plan link
    // (`/signup?plan=…` or `?plan=` in the hub) goes to the billing tab with that plan
    // selected (BillingSection scrolls to the comparison and highlights it) - otherwise
    // the choice is lost in the account hub's overview.
    // A staff preview is never a funnel arrival, and the rewrite below would
    // throw the token away - which empties the portal mid-preview and looks
    // exactly like a customer with no records. Nothing on this path applies to
    // an operator looking through a preview, so it stops here.
    if (isPreview()) return;
    if (!onSignup && !plan) return;
    const next = new URLSearchParams();
    if (plan) {
      next.set("tab", "billing");
      next.set("plan", plan);
      const cycle = sp.get("cycle");
      if (cycle === "monthly" || cycle === "yearly") next.set("cycle", cycle);
    }
    const search = next.toString();
    navigate({ pathname: "/", search: search ? `?${search}` : "" }, { replace: true });
  }

  // After a sign-in: back to the consent page the sign-in left behind (Google
  // lands on the account root, see lib/connectReturn.ts). True when it took over.
  function returnToConnect(): boolean {
    const back = takeConnectReturn();
    if (!back) return false;
    if (back !== window.location.pathname + window.location.search) navigate(back, { replace: true });
    return true;
  }

  useEffect(() => {
    getMe().then((u) => {
      setUser(u);
      if (u && !returnToConnect()) redirectFromSignupFunnel();
    });

    // The host (superadmin) theme: brand colours plus the auth layout for the login
    // and signup pages.
    api.get<SiteTheme>("/public/host-theme").then((theme) => {
      if (!theme) return;
      setHostTheme(theme);
      // The host's colours, or the operator's own on the portal host - see
      // `brandTheme`. Everything else in the theme is used as it came.
      applyThemeColorVars(document.documentElement, brandTheme(theme));
    }).catch(() => {});

    const stopScrollbars = bindScrollbarKind();
    const stopTheme = bindThemeModeEffects();
    return () => {
      stopScrollbars();
      stopTheme();
    };
  }, []);

  // Signed out on the consent page: keep its address for `returnToConnect`.
  useEffect(() => {
    if (user === null && location.pathname === "/connect") saveConnectReturn(location.pathname + location.search);
  }, [user, location.pathname, location.search]);

  // The password-reset landing page - where the e-mail link points. Independent of
  // the session (it works signed out); a successful reset revoked the other sessions
  // but spared this one (for the same user). `mode=set` ("set a password", a Google
  // user) means the session survived, so go back to the account hub's Security tab
  // rather than to the login page.
  if (location.pathname === "/reset-password") {
    const isSetup = new URLSearchParams(location.search).get("mode") === "set";
    return (
      <ResetPasswordPage
        onDone={() => {
          if (isSetup) {
            getMe().then((u) => {
              setUser(u);
              navigate(u ? "/?tab=security" : "/login", { replace: true });
            });
            return;
          }
          setUser(null);
          navigate("/login");
        }}
        authSpec={hostTheme?.auth}
      />
    );
  }

  // Accepting a client-portal invitation - where the invite e-mail's link points. It
  // creates the account and signs the person in (setUser -> the portal). Independent
  // of the session, so it works signed out.
  if (location.pathname === "/accept-invite") {
    return (
      <AcceptInvitePage
        onSuccess={(u) => {
          setUser(u);
          navigate("/", { replace: true });
        }}
        onLogin={() => {
          setUser(null);
          navigate("/login");
        }}
        authSpec={hostTheme?.auth}
      />
    );
  }

  // The account-deletion confirmation page - the second step of the deletion flow for
  // a user with no password (Google), and where the confirmation e-mail's link points.
  // Independent of the session. On confirmation the account was deactivated and every
  // session revoked, so go to the login page (`me` is empty by then).
  if (location.pathname === "/confirm-delete") {
    return (
      <ConfirmDeletePage
        onDone={() => {
          getMe().then((u) => {
            setUser(u);
            navigate(u ? "/" : "/login", { replace: true });
          });
        }}
        authSpec={hostTheme?.auth}
      />
    );
  }

  // The e-mail-change confirmation page - where the confirmation e-mail's link points
  // (the new address). It also works signed out, since the link may be opened on
  // another device. On confirmation `me` is refetched so the UI shows the new address;
  // the session stays valid, because this is not a credential change.
  if (location.pathname === "/verify-email") {
    return (
      <VerifyEmailPage
        onDone={() => {
          getMe().then((u) => {
            setUser(u);
            navigate(u ? "/" : "/login");
          });
        }}
        authSpec={hostTheme?.auth}
      />
    );
  }

  /**
   * The public document link (`/d/<token>`) - where the QR code on a printed
   * invoice or offer leads.
   *
   * Above every gate below, including the preview branch, because it is the one
   * screen in this app that is not about the signed-in person at all. The token
   * in the address is the whole credential; a session, if there is one, changes
   * nothing about what is shown. Waiting for `getMe()` here would only add a
   * round trip in front of a page a client opened from paper.
   */
  if (location.pathname.startsWith("/d/")) {
    const token = decodeURIComponent(location.pathname.slice("/d/".length)).replace(/\/+$/, "");
    if (token) return <PublicDocPage token={token} />;
  }

  /**
   * The staff preview, and it sits ABOVE the session gate on purpose.
   *
   * Not because it works signed out - it does not, the API refuses the token
   * without an operator session behind it - but because of what the two lines
   * below would otherwise do. An operator's `wa_session` IS sent to the portal
   * host (one cookie name, a `Domain` covering both), so `getMe()` answers with
   * the operator's own account and the gate would happily fall through to
   * `AccountLayout` in ORDINARY mode: their own empty portal, under a link that
   * promised the customer's. That is the bug this branch exists to prevent, and
   * it is invisible in testing by anybody who is not signed in.
   *
   * It also renders before `getMe()` settles, which is right rather than merely
   * convenient: nothing on this screen is about the signed-in person, so waiting
   * on `/me` would only cost a round trip.
   */
  if (isPreview()) {
    return <AccountLayout user={user === "loading" ? null : user} preview onLogout={() => {}} onUserChange={setUser} />;
  }

  if (user === "loading") return <Splash />;
  if (!user) {
    if (location.pathname === "/signup") {
      return (
        <SignupPage
          onSuccess={(u, opts) => {
            setUser(u);
            // Path 3 (WordPress import) goes to the "we are building your site"
            // progress view; anything else goes to the account hub. (Paths 1 and 2
            // never reach this line: SignupPage does its own full-page redirect
            // straight into the admin builder with the home page open.)
            if (opts?.importSiteId) navigate(`/import?site=${encodeURIComponent(opts.importSiteId)}`, { replace: true });
          }}
          onLoginClick={() => navigate("/login")}
          authSpec={hostTheme?.auth}
        />
      );
    }
    return (
      <LoginPage
        onSuccess={(u) => {
          setUser(u);
          returnToConnect();
        }}
        onSignupClick={() => navigate("/signup")}
        authSpec={hostTheme?.auth}
      />
    );
  }
  // The consent page an AI app (Claude and the like) sends the person to.
  if (location.pathname === "/connect") {
    return <ConnectPage authSpec={hostTheme?.auth} onSignedOut={() => setUser(null)} />;
  }
  // The post-signup WordPress import progress view (funnel path 3). A visitor with no
  // session was sent to the login page above, so only an authenticated user gets
  // here.
  if (location.pathname === "/import") {
    const siteId = new URLSearchParams(location.search).get("site");
    if (siteId) {
      return <ImportProgressPage siteId={siteId} onDone={() => navigate("/", { replace: true })} authSpec={hostTheme?.auth} funnelSpec={hostTheme?.funnel} defaultEmail={typeof user === "object" ? user.email : undefined} />;
    }
    // No site parameter: fall through to the account hub below.
  }
  return <AccountLayout user={user} onLogout={() => setUser(null)} onUserChange={setUser} />;
}

// Brand chrome before the first paint: on the operator's portal host the tab
// icon is not Husly's (see lib/brand.ts). The title follows in I18nProvider,
// because it also depends on the locale.
applyBrandFavicon();
// The operator's colours too, and before the host theme has answered: on the
// portal the first paint must not be in the kit's default indigo, and it must
// not stay there if that fetch fails. A no-op on Husly's own hosts.
const bootTheme = brandTheme(null);
if (bootTheme) applyThemeColorVars(document.documentElement, bootTheme);

const container = document.getElementById("root")! as HTMLElement & {
  __root?: ReturnType<typeof createRoot>;
};
const root = container.__root ?? (container.__root = createRoot(container));
root.render(
  <StrictMode>
    <BrowserRouter>
      <I18nProvider>
        <App />
      </I18nProvider>
    </BrowserRouter>
  </StrictMode>,
);
